/**
 * Saved run configurations for the markdown Run view — the pure half (bata-ui#132 round 2,
 * contract C1, ADR 6002).
 *
 * The product owner's words: a planner sets the parameters of a run, saves them to recall
 * later, changes them over time, and compares. The platform keeps a configuration's
 * `parameters` VERBATIM and never reads, defaults or diffs them (platform ADR 2002), so every
 * decision about what a configuration holds is made here, once, and tested on strings.
 *
 * ── A configuration is the QUESTION, not the moment ────────────────────────────────
 *
 * Saved: the market, brand, options, cluster, channel, model version, horizon, rounding,
 * the stated opening position, and the objective (kind, salvage fraction, penalty basis,
 * comparison kind) — every parameter the Run view poses. NOT saved: `runId` and `asOf`,
 * which name WHEN the question is asked, and any page state on the link (`openOption`, `carry`),
 * which is only what was open. Recalling a configuration therefore re-asks last month's question of the
 * run and date on the page now, which is what "run it again" means; a configuration that
 * pinned its run would recall an answer to a cycle that has since moved on.
 *
 * ── Verbatim, as the URL spells it ─────────────────────────────────────────────────
 *
 * Every value is the URL's own string (`weeksRemaining: "8"`, never `8`), and the option list
 * is the one array. Nothing is parsed into a number, so nothing can round, and the recalled
 * link is the saved link. An ABSENT parameter stays absent: an unstated objective saved is an
 * unstated objective recalled, which asks the platform for its governed one — exactly as the
 * unsaved page would. A value the URL leaves blank is absent, not an empty string.
 *
 * ── Comparing is a table of parameters, and nothing else ───────────────────────────
 *
 * C1 carries no solved outcome for a configuration, so a comparison lists what each one ASKED.
 * "Different" here is string inequality of two stated parameters — not a measure, not a
 * delta, not a judgement of which is better. No planning number is computed in the browser.
 */

import { OPTION_KEY_PARAM_LEGACY, OPTION_KEYS_PARAM } from './scope';

/** The surface this view's configurations are filed under (C1's `surface` discriminator). */
export const RUN_CONFIGURATION_SURFACE = 'inseason_markdown' as const;

/**
 * Every parameter a configuration holds, in the order the Run view asks for them — which is
 * also the order a comparison lists them in.
 */
export const SAVED_PARAMETERS = [
  'countryKey',
  'brandKey',
  OPTION_KEYS_PARAM,
  'clusterKey',
  'channelKey',
  'modelVersionId',
  'weeksRemaining',
  'minorUnit',
  'openingUnits',
  'objectiveKind',
  'salvageFraction',
  'penaltyBasis',
  'compareKind',
] as const;

export type SavedParameterName = (typeof SAVED_PARAMETERS)[number];

/** The market a configuration is scoped by — C1 sets `country_key` from this parameter. */
export const MARKET_PARAMETER: SavedParameterName = 'countryKey';

/**
 * A parameter value as C1 stores it: a string, number, boolean, null or a list of strings. The
 * generated client types a read-back document as `unknown` values, so this module reads
 * `unknown` and turns anything outside that shape into "not stated" rather than trusting it.
 */
export type ParameterValue = unknown;
export type Parameters = Readonly<Record<string, ParameterValue>>;

/** The platform's bounds on a name and a note (`NAME_MAX_LENGTH`, `NOTE_MAX_LENGTH`). */
export const NAME_MAX_LENGTH = 128;
export const NOTE_MAX_LENGTH = 2000;

function optionList(raw: string): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const part of raw.split(',')) {
    const key = part.trim();
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

/**
 * The page's question, from its query string: every saved parameter the URL states, as the
 * URL states it. The option list is read from the plural, or from the singular it replaced.
 */
/** The page's question as captured: URL strings, and the option list as one array. */
export type CapturedParameters = Readonly<Record<string, string | readonly string[]>>;

export function captureParameters(search: string): CapturedParameters {
  const params = new URLSearchParams(search);
  const captured: Record<string, string | string[]> = {};
  for (const name of SAVED_PARAMETERS) {
    if (name === OPTION_KEYS_PARAM) {
      const raw = params.get(OPTION_KEYS_PARAM) ?? params.get(OPTION_KEY_PARAM_LEGACY);
      const keys = raw === null ? [] : optionList(raw);
      if (keys.length > 0) captured[name] = keys;
      continue;
    }
    const value = params.get(name);
    if (value !== null && value.trim() !== '') captured[name] = value.trim();
  }
  return captured;
}

/** A stored value as the URL spells it, or `null` where there is nothing to write. */
function urlValue(value: ParameterValue): string | null {
  if (Array.isArray(value)) {
    const items = (value as readonly unknown[]).filter((item): item is string => typeof item === 'string');
    const joined = items.join(',');
    return joined === '' ? null : joined;
  }
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return null;
  const text = String(value);
  return text.trim() === '' ? null : text;
}

/**
 * The query string after recalling `parameters` onto the page at `search`.
 *
 * Every saved parameter is REPLACED — one the configuration does not state is removed, so the
 * recalled question is the saved one and not a blend of it with whatever the page held. The
 * moment (`runId`, `asOf`), the open panel and anything this module does not own are kept.
 * A parameter the configuration holds that this view does not know is not written: the URL
 * carries only what the view reads.
 */
export function applyParameters(search: string, parameters: Parameters): URLSearchParams {
  const next = new URLSearchParams(search);
  next.delete(OPTION_KEY_PARAM_LEGACY);
  for (const name of SAVED_PARAMETERS) {
    const value = urlValue(parameters[name]);
    if (value === null) next.delete(name);
    else next.set(name, value);
  }
  return next;
}

/** One line of a comparison. `null` is "not stated", never an empty string. */
export interface ParameterComparison {
  readonly name: string;
  readonly first: string | null;
  readonly second: string | null;
  readonly same: boolean;
}

/** A value as a comparison shows it: the option list as the URL spells it, text as stored. */
export function displayValue(value: ParameterValue): string | null {
  return urlValue(value);
}

/**
 * Two configurations' parameters, side by side: the view's parameters in the view's order,
 * then any other key either holds, alphabetically. A parameter neither states is first out —
 * a row of two absences says nothing.
 */
export function compareParameters(first: Parameters, second: Parameters): readonly ParameterComparison[] {
  const known = new Set<string>(SAVED_PARAMETERS);
  const others = [...new Set([...Object.keys(first), ...Object.keys(second)])]
    .filter((name) => !known.has(name))
    .sort();
  const rows: ParameterComparison[] = [];
  for (const name of [...SAVED_PARAMETERS, ...others]) {
    const a = displayValue(first[name]);
    const b = displayValue(second[name]);
    if (a === null && b === null) continue;
    rows.push({ name, first: a, second: b, same: a === b });
  }
  return rows;
}

/** Whether `search` states the same question as `parameters` — "this page is version 3". */
export function sameQuestion(search: string, parameters: Parameters): boolean {
  return compareParameters(captureParameters(search), parameters).every((row) => row.same);
}
